import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Database, Search, RefreshCw } from "lucide-react";

/**
 * Super-admin diagnostic page. Lists EVERY user document in the database
 * exactly as Mongo stores it — bypasses every filter (company_id, active).
 *
 * Use this to verify whether a "missing" employee actually exists in the
 * DB (vs. being hidden by a filter, an inactive flag, or a tenant mismatch).
 */
export default function SuperAdminAllUsers() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/all-users");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const filtered = data.users.filter((u) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return [u.email, u.first_name, u.last_name, u.company_id, u.role]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(needle));
  });

  return (
    <div className="space-y-5" data-testid="super-all-users-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Super Admin · Diagnostic</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">All Users (Raw)</h1>
          <p className="text-sm text-slate-600 mt-1">Every user document in the database. No filters. Use this to confirm if a missing employee actually exists.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-users-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Reload
        </Button>
      </header>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Total Users in DB" value={data.total_users} icon={Database} />
            <Stat label="Distinct Company IDs" value={data.distinct_company_ids.length} />
            <Stat label="Listed Below" value={filtered.length} />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">Distinct Company IDs in DB</p>
            <div className="flex flex-wrap gap-2">
              {data.distinct_company_ids.map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-mono">{c}</span>
              ))}
              {data.distinct_company_ids.length === 0 && <span className="text-xs text-slate-500">none</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, role, or company_id… (e.g. 'cameron' or 'seth')"
              className="pl-9"
              data-testid="search-users-input"
            />
          </div>

          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm" data-testid="all-users-table">
              <thead className="text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-2">Name</th>
                  <th className="text-left px-5 py-2">Email</th>
                  <th className="text-left px-5 py-2">Role</th>
                  <th className="text-left px-5 py-2">Company ID</th>
                  <th className="text-left px-5 py-2">Active</th>
                  <th className="text-left px-5 py-2">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-2 font-semibold text-slate-900">{u.first_name} {u.last_name}</td>
                    <td className="px-5 py-2 font-mono text-xs text-slate-700">{u.email}</td>
                    <td className="px-5 py-2 text-slate-700">{u.role}</td>
                    <td className="px-5 py-2 font-mono text-xs text-slate-700">{u.company_id}</td>
                    <td className="px-5 py-2">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${u.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>
                        {u.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-2 text-xs text-slate-600">{u.created_at ? fmtDate(u.created_at) : "—"}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-6 text-center text-slate-500">
                      No users match "{q}". {q && "Try a partial name or different spelling."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <p className="text-2xl font-bold font-display text-slate-900 mt-1">{value}</p>
    </div>
  );
}
